import { create } from 'zustand';
import type { FootingAnalyzeRequest } from './types';

interface FootingState {
  request: FootingAnalyzeRequest;
  setSection: (patch: Partial<FootingAnalyzeRequest['section']>) => void;
  setMaterials: (patch: Partial<FootingAnalyzeRequest['materials']>) => void;
  setSoil: (patch: Partial<FootingAnalyzeRequest['soil']>) => void;
  setLoads: (patch: Partial<FootingAnalyzeRequest['loads']>) => void;
  setReinforcement: (patch: Partial<FootingAnalyzeRequest['reinforcement']>) => void;
  loadRequest: (req: FootingAnalyzeRequest) => void;
}

const defaultRequest: FootingAnalyzeRequest = {
  section: {
    Lx: 200, Ly: 200, Hf: 60, bc: 30, hc: 30, df: 150,
    // Defaults: column centered. Set explicitly so the inputs render with
    // a numeric value; the backend still treats Lx/2, Ly/2 as the default.
    x_col: 100, y_col: 100,
  },
  materials: { fc: 210, fy: 4200, cover: 7.5 },
  soil: { sigma_adm: 1.5, gamma_s: 1800, gamma_c: 2400, sigma_factor: 1.0 },
  loads: { P: 50_000, Mx: 0, My: 0, Pu: 75_000, Mux: 0, Muy: 0 },
  reinforcement: {
    diam_x: '5/8', n_x: 10, diam_y: '5/8', n_y: 10,
    diam_col_bar: '5/8', n_col_bars: 4, col_bar_hooked: true,
  },
};

export const useFootingStore = create<FootingState>((set) => ({
  request: defaultRequest,
  setSection: (patch) =>
    set((s) => ({ request: { ...s.request, section: { ...s.request.section, ...patch } } })),
  setMaterials: (patch) =>
    set((s) => ({ request: { ...s.request, materials: { ...s.request.materials, ...patch } } })),
  setSoil: (patch) =>
    set((s) => ({ request: { ...s.request, soil: { ...s.request.soil, ...patch } } })),
  setLoads: (patch) =>
    set((s) => ({ request: { ...s.request, loads: { ...s.request.loads, ...patch } } })),
  setReinforcement: (patch) =>
    set((s) => ({
      request: { ...s.request, reinforcement: { ...s.request.reinforcement, ...patch } },
    })),
  loadRequest: (req) => set({ request: req }),
}));
